import { useMemo } from 'react';
import { Typography, Tag, Button, Tooltip } from 'antd';
import { SmartDataTableProps } from '@src/components/SmartDataTable/types';
import { useUserTimestampRender } from '@src/hooks/useUserTimestampRender';
import { usePaymentStatusRender } from './usePaymentStatusRender';

const { Text } = Typography;

interface UseInsideStockPurchasesColumnsProps {
  onCreateTransaction?: (transactionData: {
    transaction_type: 'inside-purchase';
    id: number;
    amount: number;
    type: string;
  }) => void;
}

export const useInsideStockPurchasesColumns = (
  props?: UseInsideStockPurchasesColumnsProps
): SmartDataTableProps['columns'] => {
  const { onCreateTransaction } = props || {};
  const userTimestampRender = useUserTimestampRender();
  const renderPaymentStatus = usePaymentStatusRender();

  const handleCreateTransaction = (record: any) => {
    const transactionData = {
      transaction_type: 'inside-purchase' as const,
      id: record.id,
      amount: Number(record.due_amount || record.bill_amount || 0),
      type: 'CLINIC_EXPENSES', // Set payment type to CLINIC_EXPENSES
    };

    if (onCreateTransaction) {
      onCreateTransaction(transactionData);
    }
  };

  return useMemo(
    () => [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        sorter: true,
        minWidth: 80,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'invoice_id',
        title: 'Invoice ID',
        dataIndex: 'invoice_id',
        sorter: true,
        minWidth: 150,
        ellipsis: true,
        render: (value: string) => (
          <Text style={{ fontWeight: 500, color: '#1890ff' }}>
            {value || '-'}
          </Text>
        ),
      },
      {
        key: 'supplier_info',
        title: 'Supplier Info',
        dataIndex: 'supplier',
        sorter: true,
        minWidth: 200,
        ellipsis: true,
        render: (supplier: any, record: any) => (
          <div style={{ minWidth: '180px' }}>
            <div style={{ fontWeight: 500, color: '#262626' }}>
              {supplier.name || '-'}
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
              +{supplier.phone || 'No mobile'}
            </div>
          </div>
        ),
      },
      {
        key: 'bill_amount',
        title: 'Bill Amount',
        dataIndex: 'bill_amount',
        sorter: true,
        minWidth: 150,
        render: (value: number) => (
          <Tag
            color="green"
            style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}
          >
            ₹
            {value
              ? Number(value).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                })
              : '0.00'}
          </Tag>
        ),
      },
      {
        key: 'due_amount  ',
        title: 'Due Amount',
        dataIndex: 'due_amount',
        sorter: true,
        minWidth: 150,
        render: (value: number) => (
          <Tag
            color="red"
            style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}
          >
            ₹
            {value
              ? Number(value).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                })
              : '0.00'}
          </Tag>
        ),
      },
      {
        key: 'payment_status',
        title: 'Payment Status',
        dataIndex: 'payment_status',
        sorter: false,
        minWidth: 150,
        render: (value: any, record: any) => renderPaymentStatus(value),
      },
      {
        key: 'actions',
        title: 'Payment',
        dataIndex: 'actions',
        minWidth: 120,
        render: (value: any, record: any) => {
          if (!record) return null;

          const dueAmount = Number(record.due_amount || 0);
          const canCreateTransaction = dueAmount > 0;

          if (!canCreateTransaction) return null;

          return (
            <Tooltip
              title={`Create Purchase Transaction - ₹${dueAmount.toFixed(2)} due`}
            >
              <Button
                type="primary"
                size="small"
                onClick={() => handleCreateTransaction(record)}
                style={{
                  fontSize: '11px',
                  height: '28px',
                  padding: '4px 8px',
                }}
              >
                Pay ₹{dueAmount.toFixed(2)}
              </Button>
            </Tooltip>
          );
        },
      },
      {
        key: 'created_at',
        title: 'User & Timestamp',
        dataIndex: 'created_at',
        sorter: false,
        minWidth: 180,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ],
    [userTimestampRender, renderPaymentStatus, onCreateTransaction]
  );
};
